import React, { useState, useEffect, useCallback } from 'react';
import {
  Sparkles, Layers, Gift, Mic, MapPin, ClipboardCheck, Wand2, ChevronDown, AlertTriangle, CheckCircle2, XCircle,
} from 'lucide-react';
import { personalizeCampaign, stackRewrite, preLaunchCheck, getAIUsage } from '../utils/api';
import { OfferSelector } from './OfferSelector';

const Btn = ({ icon: Icon, label, sub, onClick, busy, testId, accent }) => (
  <button
    onClick={onClick}
    disabled={busy}
    data-testid={testId}
    className={`text-left p-4 rounded-lg border transition-colors disabled:opacity-60 ${
      accent ? 'border-lime/50 bg-lime/5 hover:border-lime' : 'border-divider bg-black hover:border-lime/50'
    }`}
  >
    <p className="text-white font-bold text-sm flex items-center gap-2 mb-1">
      <Icon size={15} className={accent ? 'text-lime' : 'text-orange'} /> {label}
      {busy && <span className="inline-block w-3 h-3 border-2 border-lime border-t-transparent rounded-full animate-spin" />}
    </p>
    <p className="text-xs text-gray leading-relaxed">{sub}</p>
  </button>
);

export const AICustomizationCenter = ({ campaignId, goalData, onFilterStack, onScrollToAssets, onOpenAdvanced, onVersionsChanged }) => {
  const [busy, setBusy] = useState('');
  const [result, setResult] = useState(null);
  const [check, setCheck] = useState(null);
  const [showOffers, setShowOffers] = useState(false);
  const [msg, setMsg] = useState('');
  const [usage, setUsage] = useState(null);

  const refreshUsage = useCallback(() => {
    getAIUsage().then(({ data }) => setUsage(data)).catch(() => {});
  }, []);
  useEffect(() => { refreshUsage(); }, [refreshUsage]);

  const flash = (m) => { setMsg(m); setTimeout(() => setMsg(''), 6000); };

  const run = async (key, fn) => {
    setBusy(key);
    try { await fn(); } catch (e) {
      flash(e.response?.data?.detail || 'Something went wrong — try again.');
    } finally { setBusy(''); refreshUsage(); }
  };

  const handlePersonalize = () => run('personalize', async () => {
    const { data } = await personalizeCampaign(campaignId);
    setResult(data); setCheck(null);
    onVersionsChanged?.();
  });

  const handleStackRewrite = (mode) => run(mode, async () => {
    const { data } = await stackRewrite(campaignId, mode);
    flash(data.message);
  });

  const handleCheck = () => run('check', async () => {
    const { data } = await preLaunchCheck(campaignId);
    setCheck(data); setResult(null);
  });

  return (
    <div className="bg-card-bg border border-divider rounded-xl p-6 mb-8" data-testid="ai-customization-center">
      <div className="flex items-center justify-between flex-wrap gap-3 mb-1">
        <h3 className="text-white font-bold text-xl flex items-center gap-2">
          <Sparkles size={18} className="text-lime" /> AI Customization Center
        </h3>
        <button onClick={onOpenAdvanced} data-testid="ai-center-advanced-btn" className="text-xs text-gray hover:text-white flex items-center gap-1">
          Advanced: custom AI prompt <ChevronDown size={13} />
        </button>
      </div>
      <p className="text-sm text-gray mb-2">One-click customization powered by your Brand Profile — no prompts to write.</p>
      {usage && !usage.unlimited && (
        <p className="text-xs mb-4" data-testid="ai-usage-meter">
          <span className={usage.remaining === 0 ? 'text-orange font-bold' : 'text-gray'}>
            AI actions this cycle: <span className="text-white font-bold">{usage.used}</span> of {usage.cap} used
            {' '}· resets {new Date(usage.resets_on).toLocaleDateString(undefined, { month: 'short', day: 'numeric' })}
          </span>
        </p>
      )}
      {usage && usage.unlimited && <p className="text-xs text-gray mb-4" data-testid="ai-usage-meter">AI actions: unlimited (admin)</p>}

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-3">
        <Btn icon={Wand2} label="Personalize Campaign" accent busy={busy === 'personalize'} testId="ai-personalize-campaign-btn"
          sub="Fill your company, phone, city, offer and links into every asset. Saves as your draft — never touches the master."
          onClick={handlePersonalize} />
        <Btn icon={Layers} label="Build My Campaign Stack" testId="ai-build-stack-btn"
          sub="Show only the assets recommended for your goal, in launch order."
          onClick={() => { onFilterStack?.(); flash(goalData ? 'Showing your recommended stack below.' : 'Pick a campaign goal first — then I can build your stack.'); }} />
        <Btn icon={Gift} label="Build My Offer" testId="ai-build-offer-btn"
          sub="Pick the right offer for this campaign — it feeds every ad, email and script."
          onClick={() => setShowOffers(true)} />
        <Btn icon={Wand2} label="Customize This Asset" testId="ai-customize-asset-btn"
          sub="Use the Rewrite button on any asset card below to change one asset at a time."
          onClick={() => { onScrollToAssets?.(); flash('Scroll to any asset and hit Rewrite — urgent, premium, shorter, friendlier and more.'); }} />
        <Btn icon={Mic} label="Rewrite for My Voice" busy={busy === 'voice'} testId="ai-voice-rewrite-btn"
          sub="AI rewrites your goal's key assets in your brand tone (set it in My Brand). Runs in the background."
          onClick={() => handleStackRewrite('voice')} />
        <Btn icon={MapPin} label="Localize This Campaign" busy={busy === 'localize'} testId="ai-localize-btn"
          sub="AI weaves your city and service area through your goal's key assets. Runs in the background."
          onClick={() => handleStackRewrite('localize')} />
        <Btn icon={ClipboardCheck} label="Check Before Launch" busy={busy === 'check'} testId="ai-prelaunch-check-btn"
          sub="Six-point check: brand info, goal, offer, tracking, personalization, risky claims."
          onClick={handleCheck} />
      </div>

      {msg && <p className="text-sm text-orange mt-4" data-testid="ai-center-message">{msg}</p>}

      {result && (
        <div className="mt-5 p-4 rounded-lg bg-black border border-lime/30" data-testid="personalize-result">
          <p className="text-lime font-bold text-sm mb-2 flex items-center gap-2">
            <CheckCircle2 size={15} /> {result.personalized_count} assets personalized with your brand{result.offer_applied ? ` + your offer ("${result.offer_applied}")` : ''}.
          </p>
          {result.missing_fields.length > 0 && (
            <p className="text-xs text-light-gray mb-1" data-testid="personalize-missing-fields">
              <span className="text-orange font-semibold">Make it stronger:</span> add {result.missing_fields.join(', ')} in My Brand to unlock fuller personalization.
            </p>
          )}
          {result.warnings.length > 0 && (
            <div className="text-xs text-orange mt-2 space-y-1">
              {result.warnings.map((w) => <p key={w} className="flex items-start gap-1"><AlertTriangle size={12} className="mt-0.5 shrink-0" /> {w}</p>)}
            </div>
          )}
        </div>
      )}

      {check && (
        <div className="mt-5 p-4 rounded-lg bg-black border border-divider" data-testid="prelaunch-result">
          <p className="text-white font-bold mb-3">
            Pre-Launch Check: <span className={check.passed === check.total ? 'text-lime' : 'text-orange'}>{check.verdict}</span>
            <span className="text-gray font-normal text-sm ml-2">({check.passed}/{check.total} passed)</span>
          </p>
          <div className="space-y-2">
            {check.checks.map((c) => (
              <div key={c.label} className="flex items-start gap-2 text-sm">
                {c.ok ? <CheckCircle2 size={15} className="text-lime mt-0.5 shrink-0" /> : <XCircle size={15} className="text-orange mt-0.5 shrink-0" />}
                <span className="text-light-gray">{c.label}{!c.ok && <span className="text-gray"> — {c.fix}</span>}</span>
              </div>
            ))}
          </div>
          {check.risky_claims.length > 0 && (
            <div className="mt-3 pt-3 border-t border-divider text-xs text-orange space-y-1">
              {check.risky_claims.map((w) => <p key={w} className="flex items-start gap-1"><AlertTriangle size={12} className="mt-0.5 shrink-0" /> {w}</p>)}
            </div>
          )}
        </div>
      )}

      {showOffers && (
        <OfferSelector
          campaignId={campaignId}
          recommendedOffers={goalData?.recommended_offers || []}
          onClose={() => setShowOffers(false)}
          onSelected={() => flash('Offer saved — run "Personalize Campaign" to push it into your assets.')}
        />
      )}
    </div>
  );
};
